import React from 'react';

interface BadgeProps {
    children: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default';
    size?: 'small' | 'medium' | 'large';
    shape?: 'rounded' | 'pill' | 'square';
    outline?: boolean;
    className?: string;
    icon?: React.ReactNode;
    /** Dot indicator (V2 compat) */
    dot?: boolean;
    removable?: boolean;
    onRemove?: () => void;
    style?: React.CSSProperties;
}

const Badge: React.FC<BadgeProps> = ({
    children,
    variant = 'primary',
    size = 'medium',
    shape = 'rounded',
    outline = false,
    className = '',
    icon,
    dot = false,
    removable = false,
    onRemove,
    style,
}) => {
    const sizes = {
        small: 'text-[11px] px-2 py-0.5 gap-1',
        medium: 'text-[12px] px-2.5 py-1 gap-1.5',
        large: 'text-[13px] px-3.5 py-1.5 gap-1.5'
    };

    const shapes = {
        rounded: 'rounded-[var(--radius-sm)]',
        pill: 'rounded-full',
        square: 'rounded-[4px]'
    };

    const variants = {
        primary: outline
            ? 'border border-[var(--primary-500)]/22 text-[var(--primary-400)] bg-[var(--primary-50)]'
            : 'bg-gradient-to-b from-[var(--primary-500)] to-[var(--primary-600)] text-white shadow-[0_1px_2px_rgba(4,94,184,0.20)]',
        secondary: outline
            ? 'border border-[var(--border-glass-strong)] text-[var(--text-secondary)] bg-[var(--bg-glass)]'
            : 'bg-[var(--bg-hover)] text-[var(--text-primary)]',
        success: outline
            ? 'border border-emerald-400/22 text-emerald-400 bg-emerald-500/6'
            : 'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-[0_1px_2px_rgba(16,185,129,0.18)]',
        warning: outline
            ? 'border border-amber-400/22 text-amber-400 bg-amber-500/6'
            : 'bg-gradient-to-b from-amber-500 to-amber-600 text-white shadow-[0_1px_2px_rgba(245,158,11,0.18)]',
        danger: outline
            ? 'border border-red-400/22 text-red-400 bg-red-500/6'
            : 'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-[0_1px_2px_rgba(239,68,68,0.18)]',
        info: outline
            ? 'border border-cyan-400/22 text-cyan-400 bg-cyan-500/6'
            : 'bg-gradient-to-b from-cyan-500 to-cyan-600 text-white shadow-[0_1px_2px_rgba(6,182,212,0.18)]',
        neutral: outline
            ? 'border border-[var(--border-glass-strong)] text-[var(--text-secondary)] bg-[var(--bg-surface)]'
            : 'bg-[var(--bg-hover)] text-[var(--text-primary)]',
        default: outline
            ? 'border border-[var(--border-glass-strong)] text-[var(--text-secondary)] bg-[var(--bg-surface)]'
            : 'bg-[var(--bg-hover)] text-[var(--text-primary)]',
    };

    const iconSize = size === 'small' ? 'w-3 h-3' : size === 'large' ? 'w-4 h-4' : 'w-3.5 h-3.5';

    return (
        <span
            className={`
                inline-flex items-center font-bold tracking-[-0.005em]
                transition-all duration-[var(--duration-fast)] ease-[var(--ease-calm)]
                ${sizes[size]}
                ${shapes[shape]}
                ${variants[variant]}
                ${className}
            `}
            style={style}
        >
            {dot && (
                <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"
                />
            )}
            {icon && <span className={iconSize}>{icon}</span>}
            <span>{children}</span>
            {removable && (
                <button
                    onClick={onRemove}
                    className="ml-0.5 hover:bg-white/10 rounded-full p-0.5 transition-colors duration-150"
                    aria-label="Remove badge"
                >
                    <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}
        </span>
    );
};

export default Badge;
